import {
  closePopup,
  getDateDifferenceText,
  OverlayHoverMessage,
  PopupButtonType,
  PopupIconType,
  showPopup,
  TableSortDirection,
  Touchable,
} from "@kalyzee/kast-app-web-components";
import React, { createRef, useImperativeHandle, useRef } from "react";
import { ReactComponent as IconBuild } from "../../assets/icons/build.svg";
import { ReactComponent as IconClose } from "../../assets/icons/close.svg";
import { ReactComponent as IconData } from "../../assets/icons/data.svg";
import { ReactComponent as IconHls } from "../../assets/icons/file-hls.svg";
import { ReactComponent as IconMp4 } from "../../assets/icons/file-mp4.svg";
import { ReactComponent as IconXml } from "../../assets/icons/file-xml.svg";
import { ReactComponent as IconId } from "../../assets/icons/id.svg";
import { ReactComponent as IconTracks } from "../../assets/icons/tracks.svg";
import Colors from "../../constants/colors";
import { formatMediaEventOffset, formatMediaEventsSource, getMediaEventKindLabel } from "../../helpers/mediaEvents";
import { canBuildRecord, getRecordMp4Message, getRecordStatusLabel, isRawRecord } from "../../helpers/records";
import { getBaseUrl, getBuildVideoQueueBullMQAdminInterfaceUrl, getRecordTranscriptUrl, getSubtitleVideoQueueBullMQAdminInterfaceUrl } from "../../helpers/request";
import { OvenMediaSocketResultEmittedCommand } from "../../helpers/socket";
import {
  getSubtitlesAbsentMessage,
  getSubtitlesChip,
  getSubtitlesOverrideText,
  getSubtitlesStatusColor,
  getSubtitlesStatusLabel,
  isSubtitlesAvailable,
  isSubtitlesTerminal,
} from "../../helpers/subtitles";
import { toastError, toastSuccess, toastWarning } from "../../helpers/toast";
import { addToPathNameUrl, formatBytes } from "../../helpers/utils";
import { useSocketAppDispatch } from "../../hooks/app";
import { useRender } from "../../hooks/component";
import {
  OvenMediaContext,
  OvenMediaRecordSession,
  OvenMediaRecordSessionStatus,
  OvenMediaStreamContext,
  OvenMediaSubtitlesStatus,
  OvenMediaTrackAudio,
  OvenMediaTrackVideo,
} from "../../interfaces/context";
import { OvenMediaMediaEventsResponse, OvenMediaSubtitlesRecordResponse } from "../../interfaces/subtitles";
import TableSession from "../../pages/history/containers/table-session";
import { useOvenMediaUploadProfiles } from "../../store/context/hooks";
import { socketGetMediaEvents, socketRestartUploadRecord, socketSubtitlesRecord, SocketSubtitlesRecordParams, socketUploadRecord } from "../../store/socket/actions";
import { showSubtitlesCostPopup } from "../subtitles/SubtitlesCostPopup";
import Table, { TableColumnType, TableConf, TableConfColumn, TableContentRef, TableStyle } from "../utils/Table";
import TableAudioTrack from "./table-audio-track";
import styles from "./table-record.module.css";
import TableUpload from "./table-upload";
import TableVideoTrack from "./table-video-track";

export interface TableRecordData {
  context?: OvenMediaContext;
  stream: OvenMediaStreamContext;
  session: OvenMediaRecordSession;
}

export interface TableRecordRef {
  render: () => void;
}

export interface TableRecordProps {
  showApp?: boolean;
  data: TableRecordData[];
  sort?: { field: string; direction: -1 | 1 };
  onSort?: (field: string, direction: -1 | 1) => void;
  onStopRecord?: (item: TableRecordData) => void;
  onDeleteRecord?: (item: TableRecordData) => void;
  onBuildRecord?: (item: TableRecordData, ffmpegCmd?: string, immediate?: boolean) => void;
  onItemChecked?: (item: TableRecordData) => void;
  /** Asked after an action that changed a record on the server (a subtitles action): reload the rows. */
  onRefresh?: () => void;
  className?: string;
  style?: React.CSSProperties;
}
const TableRecord = React.forwardRef(
  (
    { showApp, sort, onSort, data, onStopRecord, onDeleteRecord, onBuildRecord, onItemChecked, onRefresh, className, style }: TableRecordProps,
    forwardRef: React.ForwardedRef<TableRecordRef | undefined>
  ) => {
    const uploadProfiles = useOvenMediaUploadProfiles();
    const containerRef = useRef<HTMLDivElement>(null);
    const render = useRender();
    const socketDispatch = useSocketAppDispatch();

    useImperativeHandle(forwardRef, () => ({
      render,
    }));

    const generateConfiguration = () => {
      const columnConfiguration: TableConfColumn<TableRecordData>[] = [
        {
          type: TableColumnType.CLASSIC,
          key: "id",
          width: "35px",
          title: "Id" /* TRANSLATION */,
          header: { className: styles.tableHeaderCellName },
          item: { className: styles.tableCellName },
        },
        {
          type: TableColumnType.CLASSIC,
          key: "server",
          width: "50px",
          title: "Server" /* TRANSLATION */,
          enableSort: false,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "key",
          width: "10rem",
          title: "Live (id / key)" /* TRANSLATION */,
          enableSort: true,
        },
        ...(showApp
          ? [
              {
                type: TableColumnType.CLASSIC,
                key: "app",
                width: "10rem",
                title: "App" /* TRANSLATION */,
                ascendantSort: (a: TableRecordData, b: TableRecordData) => a.session.app.localeCompare(b.session.app),
                descendantSort: (a: TableRecordData, b: TableRecordData) => b.session.app.localeCompare(a.session.app),
                enableSort: true,
              },
            ]
          : []),
        {
          type: TableColumnType.CLASSIC,
          key: "startedAt",
          minWidth: "13rem",
          title: "Début" /* TRANSLATION */,
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "endedAt",
          width: "15rem",
          title: "Fin" /* TRANSLATION */,
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "duration",
          width: "6rem",
          title: "Duration" /* TRANSLATION */,
          enableSort: false,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "expireAt",
          width: "15rem",
          title: "Expire le" /* TRANSLATION */,
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "deleted",
          width: "5rem",
          title: "Supprimé" /* TRANSLATION */,
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "status",
          title: "Status" /* TRANSLATION */,
          width: "8rem",
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "subtitles.status",
          title: "Sous-titres" /* TRANSLATION */,
          width: "7rem",
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "mediaEvents.count",
          title: "Événements" /* TRANSLATION */,
          width: "5.5rem",
          enableSort: true,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "tracks",
          title: "Tracks" /* TRANSLATION */,
          width: "3rem",
          enableSort: false,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "data",
          title: "Data" /* TRANSLATION */,
          width: "3rem",
          enableSort: false,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "upload",
          title: "Upload" /* TRANSLATION */,
          width: "5rem",
          enableSort: false,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "size",
          title: "Size" /* TRANSLATION */,
          width: "5rem",
          enableSort: false,
        },
        {
          type: TableColumnType.CLASSIC,
          key: "actions",
          title: "Actions" /* TRANSLATION */,
          width: "16rem",
          enableSort: false,
        },
      ];

      const defaultSort = sort ?? { field: "startedAt", direction: -1 };
      columnConfiguration.forEach((c) => {
        if (c.key === defaultSort.field) {
          c.defaultSort = defaultSort.direction === 1 ? TableSortDirection.ASC : TableSortDirection.DESC;
        }
      });

      const tableConfiguration: TableConf<TableRecordData> = {
        columns: columnConfiguration,
        header: {
          className: styles.tableHeader,
          cell: {
            className: styles.tableHeaderCell,
          },
        },
        row: {
          className: styles.tableRow,
          cell: {
            className: styles.tableRowCell,
          },
        },
        content: { className: styles.tableContent },
        valueToShowIfUndefined: { value: "-", className: styles.tableUndefinedValue },
      };

      return tableConfiguration;
    };

    // Called when a value is changed. Checkboxes here
    const valueChanged = (value: any, columnKey: string, item: TableRecordData) => {
      if (columnKey === "checked") onItemChecked?.(item);
    };

    // ── Sous-titres ──────────────────────────────────────────────────────────

    const formatDate = (value?: Date | string) => {
      if (!value) return undefined;
      const date = new Date(value);
      return Number.isNaN(date.getTime()) ? undefined : `${date.toLocaleDateString()} ${date.toLocaleTimeString()}`;
    };

    /** One per-record command; the rows are reloaded on success so the chip follows. */
    const subtitlesAction = async (
      item: TableRecordData,
      data: Omit<SocketSubtitlesRecordParams, "recordSessionId">,
      onSuccess: (response: OvenMediaSubtitlesRecordResponse) => void
    ) => {
      const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OvenMediaSubtitlesRecordResponse>>(
        socketSubtitlesRecord({ serverId: item.session._server, data: { recordSessionId: item.session.id, ...data } })
      );
      if (!result || result.error || !result.response) {
        toastError(`Sous-titres : ${result?.error?.message ?? result?.error ?? "pas de réponse"}` /* TRANSLATION */);
        return;
      }
      onSuccess(result.response);
      onRefresh?.();
    };

    const formatUntil = (response: OvenMediaSubtitlesRecordResponse) =>
      response.override ? ` — forçage jusqu'à ${new Date(response.override.until).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}` : "";

    const askTranscribeNow = (item: TableRecordData, popupIndex?: number) => {
      const redo = isSubtitlesTerminal(item.session.subtitles?.status);
      if (popupIndex !== undefined) closePopup(popupIndex);
      showSubtitlesCostPopup({
        model: item.context?.subtitles?.model,
        scope: "record",
        redo,
        allowBusy: true,
        onConfirm: (busy) =>
          subtitlesAction(item, { now: { busy }, force: redo }, (response) => {
            toastSuccess(`Transcription demandée maintenant (${response.outcome ?? "ok"})${formatUntil(response)}.` /* TRANSLATION */);
          }),
      });
    };

    const askRetranscribe = (item: TableRecordData, popupIndex?: number) => {
      if (popupIndex !== undefined) closePopup(popupIndex);
      showPopup({
        title: "Re-transcrire l'enregistrement" /* TRANSLATION */,
        iconTitle: PopupIconType.WARNING,
        content: (
          <div>
            <div className={styles.popupTitle}>{"Relancer la transcription depuis le début ?" /* TRANSLATION */}</div>
            <div className={styles.popupDescription}>
              {"Ce qu'une transcription précédente a laissé sur le disque est supprimé, et le transcript que le stockage détient sera remplacé." /* TRANSLATION */}
            </div>
            <div className={styles.popupDescription}>
              {"Le travail attend toujours une machine disponible : aucun flux en cours, et la fenêtre horaire si elle est définie. Pour passer outre, utilisez « Transcrire maintenant »." /* TRANSLATION */}
            </div>
          </div>
        ),
        buttons: [
          { type: PopupButtonType.CANCEL, element: "Non" },
          {
            type: PopupButtonType.VALIDATE,
            element: "Oui",
            onClick: async () => {
              await subtitlesAction(item, { force: true }, (response) => toastSuccess(`Transcription relancée (job ${response.jobId ?? "?"}).` /* TRANSLATION */));
              return true;
            },
          },
        ],
        enableBackdropDismiss: true,
        enableCloseButton: true,
      });
    };

    const askDeliverAgain = (item: TableRecordData, popupIndex?: number) => {
      if (popupIndex !== undefined) closePopup(popupIndex);
      subtitlesAction(item, { deliver: true }, (response) => {
        if (response.queued) toastSuccess(`${response.queued} livraison(s) du transcript en file.` /* TRANSLATION */);
        else toastWarning(`Rien à livrer : ${response.reason ?? "aucun transcript pour ce build"}.` /* TRANSLATION */);
      });
    };

    const openSubtitlesPopup = (item: TableRecordData) => {
      const session = item.session;
      const subtitles = session.subtitles;
      const context = item.context;
      // An MP4 to transcribe: `mp4` is what a build sets, `mp4Size` or a subtitles state say one
      // existed (imported or older records); the server checks the file itself.
      const available = isSubtitlesAvailable(context) && !!(session.mp4 || session.mp4Size || subtitles) && !session.deleted;
      const hasTranscript = subtitles?.status === OvenMediaSubtitlesStatus.TRANSCRIBED || subtitles?.status === OvenMediaSubtitlesStatus.DELIVERED;
      const transcriptUrl = hasTranscript ? getRecordTranscriptUrl(context, session) : undefined;
      const jobUrl = context && subtitles?.jobId ? getSubtitleVideoQueueBullMQAdminInterfaceUrl(context, subtitles.jobId) : undefined;
      const rows: [string, React.ReactNode][] = subtitles
        ? [
            ["Statut" /* TRANSLATION */, <span style={{ color: getSubtitlesStatusColor(subtitles.status), fontWeight: "bold" }}>{getSubtitlesStatusLabel(subtitles.status)}</span>],
            ["Mis à jour" /* TRANSLATION */, formatDate(subtitles.updatedAt)],
            ["Build" /* TRANSLATION */, subtitles.build ? `${formatBytes(subtitles.build.size)} — ${formatDate(new Date(subtitles.build.mtimeMs)) ?? ""}` : undefined],
            ["Job" /* TRANSLATION */, subtitles.jobId],
            ["Langue" /* TRANSLATION */, subtitles.language],
            ["Modèle" /* TRANSLATION */, subtitles.model],
            ["Morceaux" /* TRANSLATION */, subtitles.piecesTotal ? `${subtitles.piecesDone ?? 0} / ${subtitles.piecesTotal}` : undefined],
            ["Volume max" /* TRANSLATION */, subtitles.maxVolumeDb !== undefined ? `${subtitles.maxVolumeDb} dB` : undefined],
            ["En attente de" /* TRANSLATION */, subtitles.waitingFor],
            ["Erreur" /* TRANSLATION */, subtitles.error ? <span style={{ color: Colors.getTorchRed() }}>{subtitles.error}</span> : undefined],
            ["Livré le" /* TRANSLATION */, formatDate(subtitles.deliveredAt)],
            ["Forçage" /* TRANSLATION */, getSubtitlesOverrideText(subtitles)],
          ]
        : [];
      const popupIndex = showPopup({
        style: { width: "min(760px, 90%)" },
        content: (
          <div className={styles.popupSubtitlesContainer}>
            <div className={styles.popupTitle}>{"Sous-titres :" /* TRANSLATION */}</div>
            {subtitles ? (
              <div className={styles.popupFields}>
                {rows
                  .filter(([, value]) => value !== undefined && value !== "")
                  .map(([key, value]) => (
                    <div key={key} className={styles.popupFieldRow}>
                      <span className={styles.popupFieldKey}>{key}</span>
                      <span className={styles.popupFieldValue}>{value}</span>
                    </div>
                  ))}
              </div>
            ) : (
              <div className={styles.popupDescription}>{`Aucune transcription : ${getSubtitlesAbsentMessage(context)}.` /* TRANSLATION */}</div>
            )}
            {subtitles?.status === OvenMediaSubtitlesStatus.SILENT ? (
              <div className={styles.popupNote}>
                {"Enregistrement jugé silencieux : une nouvelle transcription refera l'analyse et trouvera très probablement le même silence." /* TRANSLATION */}
              </div>
            ) : null}
            {!isSubtitlesAvailable(context) ? (
              <div className={styles.popupNote}>
                {context?.subtitles?.configured === false
                  ? "Les sous-titres ne sont pas configurés sur ce serveur." /* TRANSLATION */
                  : context?.subtitles?.installed === false
                  ? "Le conteneur Whisper de ce serveur est un conteneur factice (pas de GPU) : rien ne peut être transcrit ici." /* TRANSLATION */
                  : "L'état des sous-titres de ce serveur n'est pas connu (middleware trop ancien ?)." /* TRANSLATION */}
              </div>
            ) : null}
            <div className={styles.popupActions}>
              {available ? (
                <Touchable className={[styles.popupActionButton, styles.popupActionButtonWarning].join(" ")} onPress={() => askTranscribeNow(item, popupIndex)}>
                  {"Transcrire maintenant" /* TRANSLATION */}
                </Touchable>
              ) : null}
              {available ? (
                <Touchable className={styles.popupActionButton} onPress={() => askRetranscribe(item, popupIndex)}>
                  {"Re-transcrire" /* TRANSLATION */}
                </Touchable>
              ) : null}
              {available && hasTranscript ? (
                <Touchable className={styles.popupActionButton} onPress={() => askDeliverAgain(item, popupIndex)}>
                  {"Livrer à nouveau" /* TRANSLATION */}
                </Touchable>
              ) : null}
              {transcriptUrl ? (
                <Touchable
                  className={styles.popupActionLink}
                  onPress={() => {
                    window.open(transcriptUrl, "_blank");
                  }}
                >
                  transcript.json
                </Touchable>
              ) : null}
              {jobUrl ? (
                <Touchable
                  className={styles.popupActionLink}
                  onPress={() => {
                    window.open(jobUrl, "_blank");
                  }}
                >
                  {"🎯 Job (Bull Board)" /* TRANSLATION */}
                </Touchable>
              ) : null}
            </div>
          </div>
        ),
        buttons: [{ type: PopupButtonType.OK, element: "OK" }],
      });
    };

    // ── Événements ───────────────────────────────────────────────────────────

    const openMediaEventsPopup = async (item: TableRecordData) => {
      const result = await socketDispatch<OvenMediaSocketResultEmittedCommand<any, OvenMediaMediaEventsResponse>>(
        socketGetMediaEvents({ serverId: item.session._server, data: { recordSessionId: item.session.id } })
      );
      if (!result || result.error || !result.response) {
        toastError(`Événements : ${result?.error?.message ?? result?.error ?? "pas de réponse"}` /* TRANSLATION */);
        return;
      }
      const response = result.response;
      const uploads = item.session.uploadSessions ?? [];
      const outOfSpan = response.events.some((event) => !event.inSpan);
      showPopup({
        style: { width: "min(820px, 90%)" },
        content: (
          <div className={styles.popupEventsContainer}>
            <div className={styles.popupTitle}>{`Événements (${response.count}) :` /* TRANSLATION */}</div>
            <div className={styles.popupDescription}>{formatMediaEventsSource(response.source, response.expireAt)}</div>
            {response.events.length ? (
              <div className={styles.eventsTable}>
                <div className={styles.eventsHeader}>
                  <span>{"Temps" /* TRANSLATION */}</span>
                  <span>{"Heure" /* TRANSLATION */}</span>
                  <span>{"Événement" /* TRANSLATION */}</span>
                </div>
                {response.events.map((event, index) => (
                  <div key={`${event.at}-${index}`} className={[styles.eventsRow, event.inSpan ? "" : styles.eventOutOfSpan].join(" ")}>
                    <span>{formatMediaEventOffset(event.offsetMs)}</span>
                    <span>{new Date(event.at).toLocaleTimeString()}</span>
                    <span>
                      {getMediaEventKindLabel(event.kind)}
                      <span className={styles.eventKindCode}>{event.kind}</span>
                    </span>
                  </div>
                ))}
              </div>
            ) : null}
            {outOfSpan ? (
              <div className={styles.popupNote}>
                {"Les lignes grisées sont hors de la plage de cet enregistrement : le journal est celui de la session live entière, le stockage ne garde que ce qui tombe dans le fichier." /* TRANSLATION */}
              </div>
            ) : null}
            <div className={styles.popupSubtitle} style={{ fontWeight: "bold", marginTop: 6 }}>
              {"Livraisons :" /* TRANSLATION */}
            </div>
            {uploads.length ? (
              uploads.map((upload) => (
                <div key={upload.id} className={styles.popupDescription}>
                  {`${upload.profile?.name ?? upload.profile?.host ?? upload.id} (${upload.type}) : ${
                    upload.mediaEventsDelivered === undefined
                      ? "— (enregistré avant le suivi des événements)"
                      : upload.mediaEventsDelivered
                      ? `${upload.mediaEventsDelivered} événement(s) livré(s)`
                      : "aucun événement livré"
                  }` /* TRANSLATION */}
                </div>
              ))
            ) : (
              <div className={styles.popupDescription}>{"Aucun upload." /* TRANSLATION */}</div>
            )}
            <div className={styles.popupNote}>
              {"Relancer un upload renvoie les événements avec le fichier : le stockage les inscrit dans l'objet à la réception, ils ne peuvent pas être ajoutés après coup." /* TRANSLATION */}
            </div>
          </div>
        ),
        buttons: [{ type: PopupButtonType.OK, element: "OK" }],
      });
    };

    const customRenderCell = (element: JSX.Element | null, elementRef: TableContentRef, columnKey: string, item: TableRecordData) => {
      if (columnKey === "subtitles.status") {
        const ref = createRef<HTMLDivElement>();
        const subtitles = item.session.subtitles;
        if (!subtitles) {
          const clickable = isSubtitlesAvailable(item.context) && !!(item.session.mp4 || item.session.mp4Size) && !item.session.deleted;
          return (
            <div ref={ref} className={styles.subtitlesAbsent}>
              {clickable ? <Touchable onPress={() => openSubtitlesPopup(item)}>—</Touchable> : "—"}
              <OverlayHoverMessage targetRef={ref} icon={<div />} message={getSubtitlesAbsentMessage(item.context)} />
            </div>
          );
        }
        const chip = getSubtitlesChip(subtitles);
        return (
          <div ref={ref} style={{ width: "100%", textAlign: "center" }}>
            <Touchable className={styles.subtitlesChip} style={{ backgroundColor: chip.color }} onPress={() => openSubtitlesPopup(item)}>
              {chip.text}
            </Touchable>
            {chip.hover ? <OverlayHoverMessage targetRef={ref} icon={<div />} message={chip.hover} /> : null}
          </div>
        );
      }
      if (columnKey === "mediaEvents.count") {
        const summary = item.session.mediaEvents;
        return (
          <Touchable className={styles.eventsCount} onPress={() => openMediaEventsPopup(item)}>
            {summary ? `${summary.count}` : "—"}
          </Touchable>
        );
      }
      if (columnKey === "id") {
        const ref = createRef<HTMLDivElement>();
        const id = item.session?.id ?? "";
        return (
          <div ref={ref} className={styles.name}>
            <Touchable
              className={styles.textOverflow}
              onPress={() => {
                showPopup({
                  content: (
                    <>
                      <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Id:" /* TRANSLATION */}</div>
                      <div>{id}</div>
                    </>
                  ),
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                });
              }}
            >
              <IconId width={17} height={17} />
            </Touchable>
            <OverlayHoverMessage targetRef={ref} icon={<div />} message={id} />
          </div>
        );
      }
      if (columnKey === "server") {
        const ref = createRef<HTMLDivElement>();
        const server = item.session._server;
        return (
          <div ref={ref} className={styles.key}>
            <Touchable
              className={styles.textOverflow}
              onPress={() => {
                showPopup({
                  content: (
                    <>
                      <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Server:" /* TRANSLATION */}</div>
                      <div>{server}</div>
                    </>
                  ),
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                });
              }}
            >
              {server}
            </Touchable>
            <OverlayHoverMessage targetRef={ref} icon={<div />} message={server} />
          </div>
        );
      }
      if (columnKey === "key") {
        const refKey = createRef<HTMLDivElement>();
        const key = item.stream.stream;
        const refId = createRef<HTMLDivElement>();
        const liveSession = item.session?.liveSession;
        return (
          <div className={styles.key} style={{ display: "flex", gap: "5px" }}>
            <div ref={refId}>
              <Touchable
                className={styles.textOverflow}
                onPress={() => {
                  if (liveSession) {
                    showPopup({
                      style: { width: "90%" },
                      content: <TableSession showApp={true} showServer={true} data={[liveSession]} />,
                      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                    });
                    return;
                  }
                  showPopup({
                    content: (
                      <>
                        <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Id:" /* TRANSLATION */}</div>
                        <div>{"undefined"}</div>
                      </>
                    ),
                    buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                  });
                }}
              >
                <IconId width={17} height={17} />
              </Touchable>
              <OverlayHoverMessage targetRef={refId} icon={<div />} message={liveSession?.id ?? ""} />
            </div>
            <div ref={refKey} style={{ flex: 1, minWidth: 0 }}>
              <Touchable
                className={styles.textOverflow}
                onPress={() => {
                  if (liveSession) {
                    showPopup({
                      style: { width: "90%" },
                      content: <TableSession showApp={true} showServer={true} data={[liveSession]} />,
                      buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                    });
                    return;
                  }
                  showPopup({
                    content: (
                      <>
                        <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Key:" /* TRANSLATION */}</div>
                        <div>{key}</div>
                      </>
                    ),
                    buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                  });
                }}
              >
                {key}
              </Touchable>
              <OverlayHoverMessage targetRef={refKey} icon={<div />} message={key} />
            </div>
          </div>
        );
      }
      if (columnKey === "app") {
        return (
          <Touchable
            className={[styles.app, styles.textOverflow].join(" ")}
            onPress={() => {
              showPopup({
                content: (
                  <>
                    <div style={{ marginBottom: 10 }}>{"App:" /* TRANSLATION */}</div>
                    <div>{item.session.app}</div>
                  </>
                ),
                buttons: [{ type: PopupButtonType.OK, element: "OK" }],
              });
            }}
          >
            {item.session.app}
          </Touchable>
        );
      }
      if (columnKey === "data") {
        if (!item.session?.data) return "-";
        return (
          <Touchable
            className={styles.actionPlay}
            onPress={() => {
              let data = item.session?.data;
              if (data) {
                try {
                  const json = JSON.parse(data);
                  data = JSON.stringify(json, null, 4);
                } catch (err) {}
              }

              showPopup({
                content: (
                  <div>
                    <div className={styles.popupTitle}>Data:</div>
                    {data}
                  </div>
                ),
                buttons: [{ type: PopupButtonType.OK, element: "OK" }],
              });
            }}
          >
            <IconData width={20} height={20} />
          </Touchable>
        );
      }
      if (columnKey === "actions") {
        const iconSize = 30;
        const status = item.session.status;
        const id = item.session.id;
        const hlsUrl = addToPathNameUrl(getBaseUrl(), `/record/${id}/file.m3u8`);
        const xmlUrl = addToPathNameUrl(getBaseUrl(), `/record/${id}/file.xml`);
        const mp4Url = item.session.mp4 ? addToPathNameUrl(getBaseUrl(), `/record/${id}/file.mp4`) : undefined;
        const mp4MessageRef = createRef<HTMLDivElement>();
        const raw = isRawRecord(item.session);
        if (!id) return null;
        const style: React.CSSProperties = {};
        const vodDeleted = item.session?.deleted;
        const vodDeletedMessage = () => {
          showPopup({
            title: "Vidéo supprimée",
            iconTitle: PopupIconType.INFO,
            content: `La vidéo a été supprimée.`,
            enableBackdropDismiss: true,
            enableCloseButton: true,
          });
        };
        let bullMQJobUrl: string | undefined;
        const context = item.context;
        if (context) bullMQJobUrl = getBuildVideoQueueBullMQAdminInterfaceUrl(context, item.session.currentJobId);

        return (
          <div className={styles.actions} style={style}>
            {bullMQJobUrl ? (
              <Touchable
                onPress={() => {
                  window.open(bullMQJobUrl, "_blank");
                }}
                style={{ fontSize: 26 }}
              >
                🎯
              </Touchable>
            ) : null}
            <Touchable
              onPress={() => {
                if (vodDeleted) vodDeletedMessage();
                else window.open(hlsUrl, "_blank");
              }}
            >
              <IconHls width={iconSize} height={iconSize} />
            </Touchable>
            <Touchable
              onPress={() => {
                if (vodDeleted) vodDeletedMessage();
                else window.open(xmlUrl, "_blank");
              }}
            >
              <IconXml width={iconSize} height={iconSize} />
            </Touchable>
            {mp4Url ? (
              <Touchable
                onPress={() => {
                  if (vodDeleted) vodDeletedMessage();
                  else window.open(mp4Url, "_blank");
                }}
              >
                <IconMp4 width={iconSize} height={iconSize} />
              </Touchable>
            ) : (
              <div ref={mp4MessageRef} className={styles.actionUnavailable}>
                <IconMp4 width={iconSize} height={iconSize} />
                <OverlayHoverMessage targetRef={mp4MessageRef} icon={<div />} message={getRecordMp4Message(item.session) ?? ""} />
              </div>
            )}
            {canBuildRecord(item.session) ? (
              <Touchable
                onPress={() => {
                  const inputRef = createRef<HTMLInputElement>();
                  const checkRef = createRef<HTMLInputElement>();
                  showPopup({
                    title: raw ? "Générer le MP4" : "Générer le fichier vidéo",
                    iconTitle: raw ? PopupIconType.INFO : PopupIconType.WARNING,
                    content: (
                      <div>
                        {raw ? (
                          <div className={styles.popupTitle}>Ce record est raw (segments + m3u8, pas de MP4). Générer le fichier MP4 ?</div>
                        ) : (
                          <>
                            <div className={styles.popupTitle}>Êtes-vous sûr de vouloir générer le fichier vidéo correspondant à ce record (m3u8 et mp4) ?</div>
                            <div className={styles.popupSubtitle}>Cette action détruira les fichiers déjà préalablement créés...</div>
                          </>
                        )}
                        <br />
                        <div className={styles.popupDescription}>Vous pouvez choisir quelle commande exécuter pour construire votre fichier.</div>
                        {raw ? (
                          <div className={styles.popupDescription}>
                            {"Champ vide : commande du serveur ("}
                            <code className={styles.popupCode}>VOD_BUILD_MP4_FFMPEG_COMMAND</code>)
                          </div>
                        ) : null}
                        <div className={styles.popupDescription}>
                          {"Remplacer le chemin d'entrée par "}
                          <code className={styles.popupCode}>{"<<input>>"}</code>
                        </div>
                        <div className={styles.popupDescription}>
                          {"Remplacer le chemin de sortie par "}
                          <code className={styles.popupCode}>{"<<output>>"}</code>
                        </div>
                        <div className={styles.popupDescription}>
                          <code className={styles.popupCode}>{"<<abs>>"}</code>
                          {" : "}
                          <code className={styles.popupCode}>-bsf:a aac_adtstoasc</code>
                          {" si l'audio est en AAC, rien sinon (nécessaire pour un "}
                          <code className={styles.popupCode}>-codec copy</code>)
                        </div>
                        <div className={styles.popupDescription}>
                          {"Exemple : "}
                          <code className={styles.popupCommand}>
                            {"ffmpeg -y -f concat -safe 0 -i <<input>> -codec copy <<abs>> -strict -2 -movflags +frag_keyframe+empty_moov+default_base_moof+global_sidx <<output>>"}
                          </code>
                        </div>
                        <div className={styles.popupDescription}>
                          {"En réencodage seulement : "}
                          <code className={styles.popupCode}>{"-vf '<<vf>>'"}</code>
                          {" (taille d'image constante), incompatible avec "}
                          <code className={styles.popupCode}>-codec copy</code>
                        </div>
                        <input placeholder="" className={styles.popupInput} ref={inputRef} />
                        <div style={{ width: "100%", display: "flex", marginTop: 10 }} className={styles.popupDescription}>
                          <input type="checkbox" defaultChecked={false} placeholder="" ref={checkRef} />
                          Ne pas utiliser les jobs (queue avec BullMQ)
                        </div>
                      </div>
                    ),
                    buttons: [
                      { type: PopupButtonType.CANCEL, element: "Non" },
                      {
                        type: PopupButtonType.VALIDATE,
                        element: "Oui",
                        onClick: async () => {
                          onBuildRecord?.(item, inputRef.current?.value?.trim(), checkRef.current?.checked);
                          return true;
                        },
                      },
                    ],
                    enableBackdropDismiss: true,
                    enableCloseButton: true,
                  });
                }}
              >
                <IconBuild width={iconSize} height={iconSize} />
              </Touchable>
            ) : null}
            {status === OvenMediaRecordSessionStatus.RECORDING ? (
              <Touchable
                onPress={() => {
                  showPopup({
                    title: "Arrêter l'enregistrement",
                    iconTitle: PopupIconType.WARNING,
                    content: `Êtes-vous sûr de vouloir arrêter l'enregistrement ?`,
                    buttons: [
                      { type: PopupButtonType.CANCEL, element: "Non" },
                      {
                        type: PopupButtonType.VALIDATE,
                        element: "Oui",
                        onClick: async () => {
                          onStopRecord?.(item);
                          return true;
                        },
                      },
                    ],
                    enableBackdropDismiss: true,
                    enableCloseButton: true,
                  });
                }}
              >
                <IconClose stroke={Colors.getTorchRed()} strokeWidth={2} width={iconSize} height={iconSize} />
              </Touchable>
            ) : status === OvenMediaRecordSessionStatus.TERMINATED && !item.session.deleted ? (
              <Touchable
                onPress={() => {
                  showPopup({
                    title: "Supprimer l'enregistrement",
                    iconTitle: PopupIconType.WARNING,
                    content: `Êtes-vous sûr de vouloir supprimer l'enregistrement ?`,
                    buttons: [
                      { type: PopupButtonType.CANCEL, element: "Non" },
                      {
                        type: PopupButtonType.VALIDATE,
                        element: "Oui",
                        onClick: async () => {
                          onDeleteRecord?.(item);
                          return true;
                        },
                      },
                    ],
                    enableBackdropDismiss: true,
                    enableCloseButton: true,
                  });
                }}
              >
                <IconClose stroke={Colors.getTorchRed()} strokeWidth={2} width={iconSize} height={iconSize} />
              </Touchable>
            ) : null}
          </div>
        );
      }
      if (columnKey === "tracks") {
        const tracks = item.session.tracks;
        const videoTracks: OvenMediaTrackVideo[] = [];
        const audioTracks: OvenMediaTrackAudio[] = [];

        if (tracks && item.session.tracks && item.stream.outputs) {
          item.stream.outputs.forEach((o) => {
            if (o.tracks) {
              o.tracks.forEach((track) => {
                if (tracks.includes(track.name)) {
                  if (track.type === "Video") {
                    videoTracks.push(track);
                  } else if (track.type === "Audio") {
                    audioTracks.push(track);
                  }
                }
              });
            }
          });
        }

        return (
          <Touchable
            className={styles.tracks}
            onPress={() => {
              showPopup({
                content: (
                  <div style={{ minWidth: 300 }}>
                    <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Tracks:" /* TRANSLATION */}</div>
                    {
                      <div className={styles.tracksContainer}>
                        <div>{JSON.stringify(tracks)}</div>
                        {videoTracks.length ? (
                          <>
                            <div style={{ marginTop: "10px" }} />
                            <div className={styles.tracksSeparator}>
                              <div className={styles.tracksSeparatorLine} />
                              <div className={styles.tracksSeparatorTitle}>{"Video" /* TRANSLATION */}</div>
                              <div className={styles.tracksSeparatorLine} />
                            </div>
                            <TableVideoTrack data={videoTracks} />
                          </>
                        ) : null}
                        {audioTracks.length ? (
                          <>
                            <div className={styles.tracksSeparator} style={{ marginTop: "10px" }}>
                              <div className={styles.tracksSeparatorLine} />
                              <div className={styles.tracksSeparatorTitle}>{"Audio" /* TRANSLATION */}</div>
                              <div className={styles.tracksSeparatorLine} />
                            </div>
                            <TableAudioTrack data={audioTracks} />
                          </>
                        ) : null}
                      </div>
                    }
                    <br />
                  </div>
                ),
                buttons: [{ type: PopupButtonType.OK, element: "OK" }],
              });
            }}
          >
            <IconTracks width={30} height={30} />
          </Touchable>
        );
      }
      if (columnKey === "tracks") {
        if (!item.session.tracks) return undefined;
        const ref = createRef<HTMLDivElement>();
        return (
          <div ref={ref}>
            {`[${item.session.tracks?.join(", ")}]`}
            <OverlayHoverMessage targetRef={ref} message={`Tracks: [${item.session.tracks?.join(", ")}]`} />
          </div>
        );
      }
      if (columnKey === "upload") {
        const uploads = item.session.uploadSessions;
        return (
          <Touchable
            className={styles.upload}
            onPress={() => {
              const upload = () => {
                if (!uploadProfiles?.length) {
                  toastError(`Aucun upload profile n'est encore enregistré`);
                  return;
                }
                let profileButtonContainerRef: (React.RefObject<HTMLDivElement> | null)[] = [];
                let currProfile = uploadProfiles[0];
                showPopup(
                  {
                    title: "Upload le record ?",
                    iconTitle: PopupIconType.INFO,
                    content: (
                      <div>
                        <div className={styles.popupTitle}>Choisissez l'upload profile:</div>
                        <div className={styles.popupProfileSelectionContainer}>
                          <div className={styles.popupProfileSelectionList}>
                            {uploadProfiles.map((u, i) => {
                              const ref = createRef<HTMLDivElement>();
                              const currClasses = [styles.popupProfileSelectionButton];
                              if (u === currProfile) currClasses.push(styles.popupProfileSelectionButtonSelected);
                              profileButtonContainerRef[i] = ref;
                              return (
                                <div key={u.id} ref={ref} className={currClasses.join(" ")}>
                                  <Touchable
                                    onPress={() => {
                                      currProfile = u;
                                      profileButtonContainerRef.forEach((r, j) => {
                                        const classList = r?.current?.classList;
                                        if (i === j) classList?.add(styles.popupProfileSelectionButtonSelected);
                                        else classList?.remove(styles.popupProfileSelectionButtonSelected);
                                      });
                                    }}
                                  >
                                    {`${u.name ?? `${u.host} - ${u.type}`}`}
                                  </Touchable>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    ),
                    buttons: [
                      { type: PopupButtonType.CANCEL, element: "Annuler" },
                      {
                        type: PopupButtonType.VALIDATE,
                        element: "Valider" /* TRANSLATION */,
                        onClick: async () => {
                          const profile = currProfile;
                          const result = await socketDispatch(
                            socketUploadRecord({ serverId: item.session._server, data: { recordSession: item.session.id, uploadProfile: profile.id } })
                          );
                          closePopup(popupIndex);
                          if (result.error) {
                            toastError(`Impossible de créer une nouvelle redirection. Error : ${result.error}`);
                          } else {
                            toastSuccess(
                              `L'upload a démarré. Veuillez actualiser la page pour afficher l'évolution de l'upload (cela peut prendre un certain temps).`
                            );
                          }
                          return true;
                        },
                      },
                    ],
                    enableBackdropDismiss: false,
                    enableCloseButton: false,
                  },
                  undefined,
                  { displayHover: true }
                );
              };

              const popupIndex = showPopup({
                style: { width: "90%" },
                content: (
                  <div className={styles.popupUploadContainer}>
                    <div className={styles.popupTitle}>{"Uploads:" /* TRANSLATION */}</div>
                    <div>
                      {uploads?.length ? (
                        <TableUpload
                          data={uploads.map((s) => ({ context: item.context, session: s }))}
                          onRestartUpload={async (item, immediate) => {
                            const id = item.session.id;
                            if (!id) return;
                            const result = await socketDispatch(
                              socketRestartUploadRecord({
                                serverId: item.session._server,
                                data: { uploadSession: id, immediate },
                              })
                            );
                            if (!result.error) {
                              toastSuccess(
                                `L'upload a démarré. Veuillez actualiser la page pour afficher l'évolution de l'upload (cela peut prendre un certain temps).`
                              );
                            } else {
                              toastError(`Impossible d'uploadé la vidéo pour la session ${id} : ${result.error}`);
                            }
                          }}
                        />
                      ) : (
                        <div>Aucun upload n'a été encore fait... </div>
                      )}
                    </div>
                    {uploadProfiles?.length ? (
                      <Touchable
                        className={styles.popupUpload}
                        onPress={async () => {
                          upload();
                        }}
                      >
                        Upload le record
                      </Touchable>
                    ) : null}
                  </div>
                ),
                buttons: [{ type: PopupButtonType.OK, element: "OK" }],
              });
            }}
          >
            {uploads?.length ?? 0}
          </Touchable>
        );
      }
      if (columnKey === "deleted") {
        if (item.session?.deleted) return <div style={{ color: Colors.getTorchRed() }}>true</div>;
        return <div style={{ color: Colors.getMainGreen() }}>false</div>;
      }
      if (columnKey === "size") {
        // A raw record has no mp4: its size is the backup's, the segments plus the playlist.
        const raw = isRawRecord(item.session);
        const size = raw ? item.session?.m3u8Size : item.session?.mp4Size;
        return (
          <div style={{ color: "black", whiteSpace: "pre-wrap" }}>
            <Touchable
              className={raw ? styles.sizeRaw : undefined}
              onPress={() => {
                showPopup({
                  content: (
                    <>
                      <div style={{ marginBottom: 10, fontWeight: "bold" }}>{(raw ? "Segments + m3u8 - Size:" : "M3u8 - Size:") /* TRANSLATION */}</div>
                      <div>{`${item.session?.m3u8Size ?? "-"} octets`}</div>
                      <div style={{ marginBottom: 10, fontWeight: "bold" }}>{"Mp4 - Size:" /* TRANSLATION */}</div>
                      <div>{`${item.session?.mp4Size ?? "-"} octets`}</div>
                    </>
                  ),
                  buttons: [{ type: PopupButtonType.OK, element: "OK" }],
                });
              }}
            >
              {`${size ? formatBytes(size) : "-"}`}
            </Touchable>
          </div>
        );
      }
      return element;
    };

    const addCustomStyleOnCell = (columnKey: string, item: TableRecordData) => {
      const result: TableStyle = {};
      if (columnKey === "status" && isRawRecord(item.session)) result.style = { color: Colors.getBlueCornflower() };
      return result;
    };

    const addCustomStyleOnRow = (item: TableRecordData, currData: TableRecordData[], index: number) => {
      const result: TableStyle = {};
      return result;
    };

    const onRenderTableStarts = () => {};

    const onRenderTableEnded = () => {};

    const transformValue = (columnKey: string, item: TableRecordData, initialValue: any, data: TableRecordData[], index: number) => {
      if (columnKey == "startedAt") {
        if (!item.session?.startedAt) return undefined;
        const startedAt = new Date(item.session?.startedAt);
        return `${startedAt.toLocaleDateString()} ${startedAt.toLocaleTimeString()}`;
      }
      if (columnKey == "endedAt") {
        if (!item.session?.endedAt) return undefined;
        const endedAt = new Date(item.session?.endedAt);
        return `${endedAt.toLocaleDateString()} ${endedAt.toLocaleTimeString()}`;
      }
      if (columnKey === "duration") {
        if (item.session?.startedAt && !item.session?.endedAt) return "en cours...";
        else if (!item.session?.startedAt || !item.session?.endedAt) return undefined;

        const startedAt = new Date(item.session.startedAt);
        const endedAt = new Date(item.session.endedAt);
        return getDateDifferenceText(startedAt, endedAt);
      }
      if (columnKey == "tracks") {
        return item.session.tracks ? "" : undefined;
      }
      if (columnKey == "status") {
        return getRecordStatusLabel(item.session);
      }
      if (columnKey === "subtitles.status" || columnKey === "mediaEvents.count") {
        // Rendered by customRenderCell in every case.
        return "";
      }
      if (columnKey == "expireAt") {
        if (!item.session?.expireAt) return undefined;
        const startedAt = new Date(item.session?.expireAt);
        return `${startedAt.toLocaleDateString()} ${startedAt.toLocaleTimeString()}`;
      }
      if (columnKey == "app") {
        return item.session?.app;
      }
    };

    const _onSort = (columnKey: string, direction: TableSortDirection | undefined) => {
      let numberDirection: 0 | 1 | -1 = 0;
      if (direction === TableSortDirection.ASC) numberDirection = 1;
      else if (direction === TableSortDirection.DESC) numberDirection = -1;
      if (numberDirection !== 0) onSort?.(columnKey, numberDirection);
    };

    const renderTable = () => (
      <Table
        className={styles.table}
        data={data}
        keyExtractor={(_, item) => `key-${item.session.id}`}
        configuration={generateConfiguration()}
        onRenderCellRow={customRenderCell}
        onStyleCellRow={addCustomStyleOnCell}
        onStyleRow={addCustomStyleOnRow}
        onChangeValue={valueChanged}
        onRenderStarts={() => onRenderTableStarts}
        onRenderEnded={onRenderTableEnded}
        transformValue={transformValue}
        onSort={_onSort}
      />
    );

    const classes = [styles.container];
    if (className) classes.push(className);
    return (
      <div className={classes.join(" ")} style={style} ref={containerRef}>
        {renderTable()}
      </div>
    );
  }
);

export default TableRecord;
